import React from 'react';

import {
  PageWrapper,
  useNotification,
  useModal
} from '@sopranodesign/pulse-components';
import {
  useLanguages,
  Drivers,
  useQuery,
  useMutation,
  useQueryClient
} from '@sopranodesign/pulse-hooks';
import { useAuth } from '@sopranodesign/pulse-auth';

import {
  associateLanguage as associateLanguageApi,
  removeLanguage as removeLanguageApi
} from '../api/index';

const Languages = () => {
  const { showModal, hideModal } = useModal();

  const { languages } = useLanguages();

  const { showSuccess } = useNotification();

  const { hasPermission } = useAuth();

  const queryClient = useQueryClient();

  const { data: allLanguages } = useQuery({ queryKey: ['languages/all'] });

  const { mutateAsync: associateLanguage } = useMutation({
    mutationFn: associateLanguageApi,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['languages'] });
    }
  });

  const { mutateAsync: removeLanguage } = useMutation({
    mutationFn: removeLanguageApi,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['languages'] });
    }
  });

  const handleLanguage = updatedLanguage => {
    if (languages.find(language => language.id === updatedLanguage.id)) {
      showModal({
        header: 'Remove Language',
        body: `Are you sure you want to deactivate this language? This will also delete all of your responses and utterances for this language.`,
        handleConfirm: async () => {
          try {
            await removeLanguage(updatedLanguage.id);
            showSuccess(
              `Successfully deactived the ${updatedLanguage.name} Language.`
            );
          } finally {
            hideModal();
          }
        }
      });
    } else {
      showModal({
        header: 'Add Language',
        body: `Are you sure you want to activate ${updatedLanguage.name}?`,
        handleConfirm: async () => {
          try {
            await associateLanguage(updatedLanguage.id);
            showSuccess(
              `Successfully activated the ${updatedLanguage.name} Language.`
            );
          } finally {
            hideModal();
          }
        }
      });
    }
  };

  const copy = {
    available: {
      title: 'Available languages',
      info: 'Enable one or multiple languages.'
    },
    other: {
      title: 'Other languages',
      info: 'Contact us to enable these other languages.'
    }
  };

  const canDisableLanguage = language => {
    if (!hasPermission('deactivate languages')) {
      return false;
    }

    const activeLanguage = languages.find(
      l => l.id === language.id
    );

    if (activeLanguage?.is_default) {
      return false;
    }

    if (languages.length === 1) {
      return false;
    }

    return true;
  };

  return (
    <PageWrapper header="Languages" subheader="Enable your chatbot's languages">
      <Drivers
        copy={copy}
        type="multi"
        required
        value={languages.map(language => language.id)}
        drivers={
          allLanguages?.data.map(({ preview = false, ...language }) => ({
            ...language,
            preview,
            logo: 'globe'
          })) || []
        }
        handleClick={handleLanguage}
        canEditDriver={() => false}
        canEnableDriver={() => hasPermission('activate languages')}
        canDisableDriver={canDisableLanguage}
      />
    </PageWrapper>
  );
};

export default Languages;
